import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Title from "../../components/Title";
import DashboardIcon from "@mui/icons-material/Dashboard";
import {
  Alert,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import Loading from "../../components/Loading";
import { formatDatePtBr } from "../../utils/dateUtils";
import InfoIcon from "@mui/icons-material/Info";
import CheckIcon from "@mui/icons-material/Check";
import CancelledIcon from "@mui/icons-material/Stop";
import FailedIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ButtonLoading from "../../components/ButtonLoading";
import CloseIcon from "@mui/icons-material/Close";
import { RepositoryTypes } from "../../interfaces/RepositoryTypes";
import { UserTypes } from "../../interfaces/UserTypes";
import {
  button,
  container,
  formPipeline,
  repositoryContent,
  select,
} from "./Dashboard.style";
import { colors } from "../../assets/colors";

interface Props {
  repositories: RepositoryTypes[];
  loading: boolean;
  repositorySelected: any;
  setRepositorySelected: any;
  runs: any;
  handleSubmit: any;
  refresh: any;
  childKey: any;
  open: any;
  setOpen: any;
  cancelRun: any;
  canceling: any;
  rerun: any;
  userData: UserTypes;
}

export default function DashboardScreen({
  repositories,
  loading,
  repositorySelected,
  setRepositorySelected,
  runs,
  handleSubmit,
  refresh,
  childKey,
  open,
  setOpen,
  cancelRun,
  canceling,
  rerun,
  userData,
}: Props) {
  const renderStatusBedge = (status: string) => {
    let color;

    switch (status) {
      case "success":
        color = "#00B132";
        break;
      case "failure":
        color = "#B10000";
        break;
      case "cancelled":
        color = "#989898";
        break;
      default:
        color = "#002FAA";
        break;
    }

    return (
      <div
        style={{
          display: "flex",
          backgroundColor: color,
          width: 6,
          borderRadius: 8,
          height: 160,
        }}
      />
    );
  };

  const renderStatusTest = (status: string) => {
    let text;

    switch (status) {
      case "success":
        text = "✅ Sucesso";
        break;
      case "failure":
        text = "🚨 Falha";
        break;
      case "cancelled":
        text = "⚫️ Cancelado";
        break;
      default:
        text = "🔄 Executando...";
        break;
    }

    if (status === "in_progress" || status == null) {
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={require("../../assets/running.gif")}
            style={{ width: 32, height: 32 }}
          />
          <Typography
            key={new Date().getTime()}
            component="span"
            variant="subtitle2"
            sx={{
              marginLeft: 1,
              width: 100,
              fontSize: 14,
              fontWeight: "400",
              color: "#000000",
            }}
          >
            Executando...
          </Typography>
        </div>
      );
    } else {
      return (
        <Typography
          key={new Date().getTime()}
          component="span"
          variant="subtitle2"
          sx={{
            marginLeft: 2,
            width: 100,
            fontSize: 14,
            fontWeight: "400",
            color: "#000000",
          }}
        >
          {text}
        </Typography>
      );
    }
  };

  const renderJob = (job: any, index: number) => {
    let color;
    let icon;

    if (job.status === "completed") {
      if (job.conclusion === "success") {
        color = colors.status.green;
        icon = (
          <CheckIcon style={{ color: colors.white, width: 16, height: 16 }} />
        );
      } else {
        icon = (
          <FailedIcon style={{ color: colors.white, width: 16, height: 16 }} />
        );
        color = colors.status.red;
      }
    } else if (job.status === "in_progress") {
      color = colors.status.blue;
      icon = (
        <RefreshIcon style={{ color: colors.white, width: 16, height: 16 }} />
      );
    } else if (job.status === "pending") {
      color = colors.gray;
      icon = (
        <CancelledIcon style={{ color: colors.white, width: 16, height: 16 }} />
      );
    }

    return (
      <div
        key={index + new Date().getTime()}
        style={{ width: 120, marginLeft: 20 }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 100,
            backgroundColor: color,
            zIndex: 1,
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
          }}
        >
          {icon}
        </div>
        <div
          style={{
            position: "relative",
            top: -10,
            width: 110,
            height: 1,
            backgroundColor: colors.background,
            marginLeft: 24,
          }}
        />
        <h1
          style={{
            fontSize: 10,
            fontWeight: "400",
            color: colors.black,
            width: "75%",
          }}
        >
          {job.name}
        </h1>
      </div>
    );
  };

  const renderEmptyState = () => {
    return (
      <Box
        style={{
          width: "90%",
          marginTop: 12,
          marginLeft: 24,
          padding: 12,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          alignSelf: "center",
          height: "40vh",
          backgroundColor: colors.background,
        }}
      >
        <InfoIcon />
        <span style={{ color: "#5A5361FF", marginLeft: 10, width: "40%" }}>
          Selecione um repositório para ver as execuções. Caso não tenha
          cadastrado, cadastre no menu "Repositórios".
        </span>
      </Box>
    );
  };

  const renderNeedGithubInfo = () => {
    return (
      <Box
        style={{
          width: "90%",
          marginTop: 12,
          marginLeft: 24,
          padding: 12,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          alignSelf: "center",
          height: "40vh",
          backgroundColor: "#FCE3AEFF",
        }}
      >
        <InfoIcon />
        <span style={{ color: "#536157", marginLeft: 10, width: "40%" }}>
          Cadastre seu usuário do Github e seu token para criar e ver as
          execuções. Acesse o menu Perfil para cadastrar essas informações.
        </span>
      </Box>
    );
  };

  return (
    <Box sx={container}>
      <Toolbar />
      <Title text="Dashboard" icon={<DashboardIcon color="primary" />} />

      <Box sx={repositoryContent}>
        <FormControl fullWidth sx={formPipeline}>
          <InputLabel>Selecione o repositório...</InputLabel>
          <Select
            value={repositorySelected}
            label="Selecione o repositório..."
            onChange={(e) => setRepositorySelected(e.target.value)}
            sx={select}
          >
            {repositories.map((repo, index) => (
              <MenuItem key={index} value={repo.nick_name}>
                {repo.nick_name}
              </MenuItem>
            ))}
          </Select>

          <ButtonLoading
            label="Executar pipeline"
            loading={loading}
            style={button}
            onClick={handleSubmit}
          />
        </FormControl>

        <Box
          style={{
            backgroundColor: "#FFD6D6",
            width: 650,
            marginTop: 12,
            padding: 12,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <InfoIcon />
          <span style={{ color: "#536157", marginLeft: 10 }}>
            Ao criar uma execução, pode levar alguns segundos até que apareça na
            tela. A execução aparece automaticamente!
          </span>
        </Box>

        <Collapse in={open}>
          <Alert
            severity="info"
            style={{ width: "40%", marginTop: 16, borderRadius: "8px" }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            A execução foi enviada. Aguarde enquanto é criada...
          </Alert>
        </Collapse>
      </Box>

      {loading && <Loading text={"Carregando..."} color={colors.primary} />}

      <Box
        component="div"
        sx={{
          justifyContent: "center",
          alignSelf: "center",
          paddingLeft: "24px",
        }}
      >
        {userData &&
          userData?.githubUser! &&
          userData?.githubToken! &&
          !repositorySelected! &&
          renderEmptyState()}

        {!userData?.githubUser! &&
          !userData?.githubToken! &&
          renderNeedGithubInfo()}

        {!loading &&
          runs.map((run: any, index: number) => (
            <Box
              key={childKey + index}
              component="div"
              sx={{
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: "#F4F4F4",
                borderRadius: 4,
                marginTop: 2,
                display: "flex",
                flexDirection: "row",
                justifyContent: "start",
                alignItems: "start",
                padding: "16px",
                width: "95%",
              }}
            >
              {renderStatusBedge(run.conclusion)}

              <Box
                component="div"
                sx={{
                  justifyContent: "start",
                  alignItems: "start",
                  display: "flex",
                  flexDirection: "column",
                  width: "90%",
                  marginLeft: 2,
                }}
              >
                <Typography
                  component="span"
                  variant="subtitle2"
                  sx={{
                    marginLeft: 2,
                    fontSize: 12,
                    fontWeight: "600",
                    color: "#000000",
                  }}
                >
                  {run.display_title}
                  <Typography
                    component="span"
                    variant="subtitle2"
                    sx={{
                      marginLeft: 2,
                      fontSize: 12,
                      fontWeight: "400",
                      color: "#000000",
                    }}
                  >
                    {formatDatePtBr(
                      run.run_started_at.replace("T", " ").replace("Z", "")
                    )}
                  </Typography>
                </Typography>

                <Box
                  component="div"
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "start",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  {renderStatusTest(run.conclusion)}
                </Box>

                <Box
                  component="div"
                  key={index + childKey}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "start",
                    alignItems: "start",
                    padding: "16px",
                    width: "98%",
                    mt: 2,
                  }}
                >
                  {run.jobs[0].steps.map((job: any, index: number) =>
                    renderJob(job, index)
                  )}
                </Box>

                {run.status != "completed" && (
                  <ButtonLoading
                    style={{
                      height: "40px",
                      marginTop: 3,
                      borderRadius: "8px",
                      backgroundColor: "#C51C1C",
                    }}
                    label="Cancelar execução"
                    styleLabel={{ fontSize: 12 }}
                    onClick={() => cancelRun(run.cancel_url)}
                    loading={canceling}
                  />
                )}

                {run.status == "completed" && (
                  <ButtonLoading
                    style={{
                      height: "40px",
                      marginTop: 3,
                      borderRadius: "8px",
                      backgroundColor: "#299D22",
                    }}
                    label="Re-run"
                    styleLabel={{ fontSize: 12 }}
                    onClick={() => rerun(run.rerun_url)}
                    loading={canceling}
                  />
                )}
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
}
